import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { buildGatewayUrl } from "@arcadeai/ui-kit/mcp-gateway-connect";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { getBindingInfo } from "@/lib/binding";
import { getRelativeTimeString } from "@/lib/dates";
import { useEnginePublicUrl } from "../../api/get-app-config";
import type { McpServer } from "../../types";
import { AllowedToolsCell } from "./components/allowed-tools-cell";
import { McpGatewayActions } from "./mcp-gateway-actions";

function GatewayUrlCell({ slug }: { slug: string | undefined }) {
  const enginePublicUrl = useEnginePublicUrl();

  if (!slug) {
    return <span className="text-muted-foreground">No URL</span>;
  }

  const fullUrl = buildGatewayUrl(enginePublicUrl, slug);
  return (
    <TooltipProvider delay={200}>
      <div className="group flex w-72 items-center gap-2">
        <Tooltip>
          <TooltipTrigger render={<span className="truncate" />}>
            {fullUrl}
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">
            <p className="break-all text-sm">{fullUrl}</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger render={<span />}>
            <CopyButton
              className="h-7 w-7 opacity-60 transition-opacity group-hover:opacity-100"
              content={fullUrl}
              variant="ghost"
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>Copy URL</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

export const gatewayColumns: ColumnDef<McpServer>[] = [
  {
    id: "id",
    accessorKey: "id",
    meta: { displayName: "ID", defaultVisible: false },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="ID" />
    ),
    cell: ({ row }) => <span>{row.original.id}</span>,
  },
  {
    id: "name",
    accessorKey: "name",
    meta: { displayName: "Name", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => <span>{row.original.name}</span>,
  },
  {
    id: "description",
    accessorKey: "description",
    meta: { displayName: "Description", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Description" />
    ),
    cell: ({ row }) => {
      const description = row.original.description;
      if (!description) {
        return <span className="text-muted-foreground">—</span>;
      }
      return (
        <TooltipProvider delay={200}>
          <Tooltip>
            <TooltipTrigger
              render={
                <span className="line-clamp-2 max-w-md cursor-help overflow-hidden text-ellipsis" />
              }
            >
              {description}
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p className="whitespace-pre-wrap text-sm">{description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    },
  },
  {
    id: "url",
    accessorKey: "slug",
    meta: { displayName: "URL", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="URL" />
    ),
    cell: ({ row }) => <GatewayUrlCell slug={row.original.slug} />,
  },
  {
    id: "binding_type",
    accessorKey: "binding.type",
    meta: { displayName: "Type", defaultVisible: false },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    cell: ({ row }) => {
      const info = getBindingInfo(row.original.binding.type);
      const { background, hoverBackground, ring, badgeText, badgeBorder } =
        info.classes;
      const className = `${background} ${hoverBackground} ${ring} ${badgeText} ${badgeBorder}`;
      return (
        <Badge className={className} variant="secondary">
          {info.displayName}
        </Badge>
      );
    },
  },
  {
    id: "status",
    accessorKey: "status",
    meta: { displayName: "Status", defaultVisible: false },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      const status = row.original.status;
      const isActive = status === "active";
      const className = isActive
        ? "bg-green-100 text-green-800 border-green-200"
        : "bg-red-100 text-red-800 border-red-200";
      const label = isActive ? "Active" : "Inactive";
      return (
        <Badge className={className} variant="secondary">
          {label}
        </Badge>
      );
    },
  },
  {
    id: "allowed_tools",
    accessorKey: "tool_filter.allowed_tools",
    meta: { displayName: "Allowed Tools", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Allowed Tools" />
    ),
    cell: ({ row }) => (
      <AllowedToolsCell tools={row.original.tool_filter?.allowed_tools ?? []} />
    ),
  },
  {
    id: "created_at",
    accessorKey: "created_at",
    meta: { displayName: "Created", defaultVisible: false },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => {
      const d = new Date(row.original.created_at);
      const text = Number.isNaN(d.getTime())
        ? row.original.created_at
        : getRelativeTimeString(d);
      return <span>{text}</span>;
    },
  },
  {
    id: "updated_at",
    accessorKey: "updated_at",
    meta: { displayName: "Updated", defaultVisible: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Updated" />
    ),
    cell: ({ row }) => {
      const d = new Date(row.original.updated_at);
      const text = Number.isNaN(d.getTime())
        ? row.original.updated_at
        : getRelativeTimeString(d);
      return <span>{text}</span>;
    },
  },
  {
    id: "actions",
    meta: {
      displayName: "Actions",
      defaultVisible: true,
    },
    header: () => <div className="flex items-center space-x-2">Actions</div>,
    cell: ({ row }) => <McpGatewayActions gateway={row.original} />,
  },
];
